import { isEqual } from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import MultiInputField from '../../common/MultiInputField';
const StyledDiv = styled.div `
  .helper-text {
    display: flex;
    align-items: center;
    padding: 4px;
    color: rgba(0, 0, 0, 0.54);
  }
  .items {
    flex: 1;
    width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-right: 8px;
    font-size: 0.75rem;
  }
  .edit-button {
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  }
`;
export const EditEnumDialog = props => {
    const { open, items: initialItems, onClose, onChange } = props;
    const [items, setItems] = useState(initialItems);
    const didChange = useMemo(() => {
        return !isEqual(items, initialItems);
    }, [items, initialItems]);
    return (React.createElement(Dialog, { open: open, onClose: onClose, fullWidth: true, maxWidth: 'xs' },
        React.createElement(DialogTitle, null, "Edit Enum Items"),
        React.createElement(DialogContent, null,
            React.createElement(MultiInputField, { options: initialItems, hideKeyPairType: true, onChange: items => setItems(items.map(item => item.value)) })),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: onClose }, "Cancel"),
            React.createElement(Button, { color: 'primary', variant: 'contained', disabled: !didChange, onClick: () => onChange === null || onChange === void 0 ? void 0 : onChange(items) }, "Save Changes"))));
};
export const EntityEnumConfigurator = props => {
    const { picker, column, disabled, onChange } = props;
    const [open, setOpen] = useState(false);
    const items = useMemo(() => {
        return Array.isArray(column.enum) ? column.enum : [];
    }, [column]);
    const displayedItems = useMemo(() => {
        if (!column.enum ||
            (Array.isArray(column.enum) && column.enum.length === 0)) {
            return 'No items configured.';
        }
        if (Array.isArray(column.enum)) {
            return column.enum.join(', ');
        }
        return column.enum.toString();
    }, [column]);
    return (React.createElement(StyledDiv, { disabled: disabled },
        React.createElement("div", null, picker),
        React.createElement("div", { className: 'helper-text' },
            React.createElement(Typography, { className: 'items', variant: 'caption', title: displayedItems }, displayedItems),
            React.createElement(Link, { className: 'edit-button', onClick: () => setOpen(true) }, "Click to edit")),
        React.createElement(EditEnumDialog, { open: open, items: items, onClose: () => setOpen(false), onChange: items => {
                setOpen(false);
                onChange(Object.assign(Object.assign({}, column), { type: 'enum', enum: items }));
            } })));
};
export const EntityEnumField = ({ plugin, column, value, label, placeholder, error, helperText, onChange }) => {
    var _a, _b;
    const validationError = (_b = (_a = plugin.edit) === null || _a === void 0 ? void 0 : _a.validateError) === null || _b === void 0 ? void 0 : _b.call(_a, column, value);
    const enums = Array.isArray(column.enum) ? column.enum : [];
    const options = enums;
    return (React.createElement(Autocomplete, { forcePopupIcon: true, fullWidth: true, disableClearable: true, 
        // disabled={disabled}
        options: options || [], value: value || null, onChange: (_, value) => {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }, renderInput: params => (React.createElement(TextField, Object.assign({ className: 'entity-field' }, params, { fullWidth: true, size: 'small', label: label, placeholder: placeholder || 'Select a value', error: Boolean(error) || Boolean(validationError), helperText: validationError || helperText, variant: 'outlined' }))) }));
};
// update other fields so the total is 100
function distributePercent(enums, skipItem) {
    const copy = [...enums];
    let diff = 100 - copy.reduce((total, { percent }) => total + percent, 0);
    for (const item of copy) {
        if (diff === 0) {
            break;
        }
        if (item === skipItem) {
            continue;
        }
        const value = item.percent + diff;
        diff = value < 0 ? value : value > 100 ? value - 100 : 0;
        item.percent = value < 0 ? 0 : value > 100 ? 100 : value;
    }
    return copy;
}
export const EntitySampleEnumConfigurator = props => {
    const { column, config, disabled, validate, onChange } = props;
    const errors = (validate === null || validate === void 0 ? void 0 : validate(config, column)) || {};
    return (React.createElement(Grid, { container: true, spacing: 2 }, config.enum.map((item, index) => {
        var _a;
        return (React.createElement(Grid, { item: true, xs: 12, md: 6, key: `${item.value}-${index}` },
            React.createElement(TextField, { fullWidth: true, size: 'small', type: 'number', variant: 'outlined', label: item.value, disabled: disabled, error: !!errors.enum, value: ((_a = item.percent) !== null && _a !== void 0 ? _a : '').toString(), helperText: index === config.enum.length - 1 &&
                    (errors.enum || 'Set the random percentage'), InputProps: {
                    startAdornment: (React.createElement(Typography, { color: 'textSecondary', style: { marginRight: 6 } }, "%"))
                }, onChange: event => {
                    const copy = [...config.enum];
                    const item = copy[index];
                    const value = event.target.value
                        ? parseInt(event.target.value)
                        : 0;
                    item.percent = value < 0 ? 0 : value > 100 ? 100 : value;
                    onChange === null || onChange === void 0 ? void 0 : onChange({ enum: distributePercent(copy, item) });
                } })));
    })));
};
export const EntityEnumPlugin = {
    type: 'enum',
    configurator: EntityEnumConfigurator,
    onRemoveColumnType: (column, index, { trackChanges }) => {
        if (column.type !== 'enum') {
            // remove column enum when type is changed from enum to other type
            delete column.enum;
            trackChanges.delete(`columns[${index}].enum`);
        }
        return column;
    },
    onSetColumnType: (column, index, { trackChanges }) => {
        trackChanges.update(`columns[${index}].enum`, []);
        return column;
    },
    edit: {
        field: EntityEnumField
    },
    sampleData: {
        configurator: EntitySampleEnumConfigurator,
        defaultValue: column => {
            const enums = Array.isArray(column.enum) ? column.enum : [];
            const percent = enums.length > 0 ? Math.floor(100 / enums.length) : 100;
            const values = enums.map(value => ({ value, percent }));
            return { enum: distributePercent(values) };
        },
        validate: config => {
            var _a;
            if (!((_a = config.enum) === null || _a === void 0 ? void 0 : _a.length)) {
                return;
            }
            const total = config.enum.reduce((total, { percent }) => total + percent, 0);
            if (total !== 100) {
                return { enum: `Total percent is ${total}% but should be 100%.` };
            }
        }
    }
};
//# sourceMappingURL=EntityEnumPlugin.js.map